import { Stack, Typography } from '@mui/material';
import {
  type RestoreAccount,
  type SourceAccount,
  CloudAccountType,
  Provider,
  type Account,
} from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import {
  getAccountProvider,
  getAccountType,
  isRestoreAccount,
  isSourceAccount,
} from '@/utils/accounts';

import { CopyToClipboardWrapper } from '../buttons/copyToClipboardButton';
import { OptionMenu } from '../option-menu/option-menu';
import { EditEntityName } from '../wizard/renameEntityName';

export const AccountNameOptionMenu = ({
  account,
  onEditNameOptionClick,
  mutate,
  editMode,
}: {
  account: Account | SourceAccount | RestoreAccount;
  onEditNameOptionClick: () => void;
  mutate: () => Promise<void>;
  editMode: boolean;
}) => {
  const { isAuthorized } = useRoles();
  const dal = useDAL();
  const accountType = getAccountType(account);
  const cloudProvider = getAccountProvider(account);

  const onSyncToAccountAliasOptionClick = async () => {
    const emptyName = '';
    if (accountType === CloudAccountType.Restore) {
      await dal.cloudAccounts.restore.name.update(account.id, emptyName);
    } else {
      await dal.cloudAccounts.source.update(account.id, { name: emptyName });
    }
    await mutate();
  };

  const entityName =
    cloudProvider === Provider.Gcp || cloudProvider === Provider.MongoAtlas
      ? 'project name'
      : cloudProvider === Provider.Azure
        ? 'subscription name'
        : 'account alias';

  const authorizationScope =
    accountType === CloudAccountType.Restore
      ? 'update:restore_accounts'
      : 'update:source_accounts';

  const isUsingCustomName = !!(isSourceAccount(account) ||
  isRestoreAccount(account)
    ? account.alias
    : account.eonCustomAccountName);

  return (
    !editMode && (
      <Stack direction='row' alignItems='center' gap='12px'>
        <Typography color='var(--mui-palette-text-disabled)'>
          {isUsingCustomName ? 'Customized' : 'Synced'}
        </Typography>
        <OptionMenu
          options={[
            {
              id: 'sync-from-account-alias',
              text: `Sync from ${entityName}`,
              isAuthorized: isAuthorized(authorizationScope),
              menuItemProps: {
                disabled:
                  !isAuthorized(authorizationScope) || !isUsingCustomName,
                onClick: () => void onSyncToAccountAliasOptionClick(),
              },
            },
            {
              id: 'edit-custom-name',
              text: 'Edit custom name',
              isAuthorized: isAuthorized(authorizationScope),
              menuItemProps: {
                disabled: !isAuthorized(authorizationScope),
                onClick: () => onEditNameOptionClick(),
              },
            },
          ]}
        />
      </Stack>
    )
  );
};

export const RenameAccount = ({
  account,
  mutate,
  editMode,
  onClose,
}: {
  account: Account | SourceAccount | RestoreAccount;
  mutate: () => Promise<void>;
  editMode: boolean;
  onClose: () => void;
}) => {
  const dal = useDAL();

  const accountType = getAccountType(account);
  const onSave = async (name: string) => {
    if (accountType === CloudAccountType.Restore) {
      await dal.cloudAccounts.restore.name.update(account.id, name);
    } else {
      await dal.cloudAccounts.source.update(account.id, { name });
    }
    await mutate();
  };

  return editMode &&
    (accountType === CloudAccountType.Source ||
      accountType === CloudAccountType.Restore) ? (
    <EditEntityName
      initialName={account.name}
      onSubmit={(name) => void onSave(name)}
      onClose={onClose}
    />
  ) : (
    <CopyToClipboardWrapper textToCopy={account.name}>
      <Typography className='truncate'>{account.name}</Typography>
    </CopyToClipboardWrapper>
  );
};
